// i18n.js - Enhanced with proper localStorage and RTL support
import i18n, { InitOptions } from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import enTranslations from "./locales/en.json";
import arTranslations from "./locales/ar.json";

// Custom language storage key
const LANGUAGE_KEY = "i18nextLng";

// Handle direction and body classes
const handleLanguageChange = (lng: string) => {
  const isRTL = lng === "ar";

  // Set document attributes
  document.documentElement.dir = isRTL ? "rtl" : "ltr";
  document.documentElement.lang = lng;

  // Manage body classes
  if (isRTL) {
    document.body.classList.add("rtl");
    document.body.classList.remove("ltr");
  } else {
    document.body.classList.add("ltr");
    document.body.classList.remove("rtl");
  }

  // Ensure language is saved to localStorage
  try {
    localStorage.setItem(LANGUAGE_KEY, lng);
  } catch (error) {
    console.warn("Failed to save language to localStorage:", error);
  }
};

// Get saved language or fallback
const getSavedLanguage = () => {
  try {
    return localStorage.getItem(LANGUAGE_KEY) || "en";
  } catch (error) {
    console.warn("Failed to get language from localStorage:", error);
    return "en";
  }
};

// Initialize i18n
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init<InitOptions>({
    resources: {
      en: {
        translation: enTranslations,
      },
      ar: {
        translation: arTranslations,
      },
    },
    lng: getSavedLanguage(), // Set initial language from localStorage
    fallbackLng: "en",
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      lookupLocalStorage: LANGUAGE_KEY,
      caches: ["localStorage"],
    },
    // whitelist: ["en", "ar"], // Only allow these languages
    react: {
      useSuspense: true,
    },
  })
  .then(() => {
    // Set initial direction after i18n is initialized
    handleLanguageChange(i18n.language);
  })
  .catch((error) => {
    console.error("i18n initialization failed:", error);
    // Fallback to English if initialization fails
    handleLanguageChange("en");
  });

// Listen for language changes
i18n.on("languageChanged", (lng) => {
  handleLanguageChange(lng);
});

// Handle browser refresh - ensure direction is set correctly
window.addEventListener("DOMContentLoaded", () => {
  const currentLang = i18n.language || getSavedLanguage();
  handleLanguageChange(currentLang);
});

export default i18n;
